<template>
  <div class="container">

    <!-- Institution Selector -->
    <b-row class="mt-2 mb-3">
      <b-col md="6">
        <label
          class="text-white-50 d-block mb-1"
          style="font-size:0.85rem; letter-spacing:0.04em;"
        >
          <feather-icon
            icon="BookmarkIcon"
            size="14"
            class="mr-1"
          />
          Select Institution
        </label>
        <v-select
          v-if="institutions.length > 0"
          v-model="selectedInstitutionId"
          :dir="'ltr'"
          :options="institutions"
          :reduce="val => val.id"
          :label="'name'"
          placeholder="Overall Institutions"
          class="w-100 mb-0 institution-select"
          :disabled="institutionsLoading"
        >
          <template v-slot:option="option">
            {{ option.name }}
          </template>
          <template #selected-option="{ name }">
            <div style="display: flex; align-items: baseline">
              {{ name }}
            </div>
          </template>
          <template #no-options>
            No matching institutions
          </template>
        </v-select>
      </b-col>

      <!-- Loader for institutions dropdown -->
      <b-col
        v-if="institutionsLoading"
        md="1"
        class="d-flex align-items-center"
      >
        <b-spinner
          small
          variant="primary"
          label="Loading institutions"
        />
      </b-col>
    </b-row>

    <div style="position: relative; min-height: 400px;">
      <!-- Page loader overlay (absolutely positioned to prevent DOM unmounting lag) -->
      <div
        v-if="dashboardLoading"
        class="dashboard-loader d-flex flex-column align-items-center justify-content-center"
        style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 16, 27, 0.9); z-index: 99; border-radius: 14px; min-height: 400px;"
      >
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
          label="Loading dashboard"
        />
        <p class="text-white-50 mt-3">
          Loading dashboard data...
        </p>
      </div>

      <!-- Main Dashboard Content (kept mounted for smooth chart transition animations) -->
      <div :style="dashboardLoading ? 'pointer-events: none;' : ''">

        <!-- Summary Stats -->
        <b-row
          v-if="chartSummary"
          class="mt-2"
        >
          <!-- Total Sessions -->
          <b-col
            :md="hasPermission('Token Usage Institution') ? 3 : 4"
            class="mb-2"
          >
            <div
              class="stat-card p-3 d-flex align-items-center"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px);"
            >
              <div
                class="stat-icon mr-2 rounded-circle d-flex justify-content-center align-items-center"
                style="background: rgba(115, 103, 240, 0.2); width: 50px; height: 50px;"
              >
                <feather-icon
                  icon="ActivityIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
              <div>
                <h3 class="text-light mb-0">
                  {{ chartSummary.total_sessions }}
                </h3>
                <span class="text-white-50">Total Sessions</span>
              </div>
            </div>
          </b-col>
          <!-- Total Duration -->
          <b-col
            :md="hasPermission('Token Usage Institution') ? 3 : 4"
            class="mb-2"
          >
            <div
              class="stat-card p-3 d-flex align-items-center"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px);"
            >
              <div
                class="stat-icon mr-2 rounded-circle d-flex justify-content-center align-items-center"
                style="background: rgba(40, 199, 111, 0.2); width: 50px; height: 50px;"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="24"
                  class="text-success"
                />
              </div>
              <div>
                <h3 class="text-light mb-0">
                  {{ chartSummary.total_duration }}
                </h3>
                <span class="text-white-50">Total Duration</span>
              </div>
            </div>
          </b-col>
          <!-- Active Users -->
          <b-col
            :md="hasPermission('Token Usage Institution') ? 3 : 4"
            class="mb-2"
          >
            <div
              class="stat-card p-3 d-flex align-items-center"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px);"
            >
              <div
                class="stat-icon mr-2 rounded-circle d-flex justify-content-center align-items-center"
                style="background: rgba(255, 159, 67, 0.2); width: 50px; height: 50px;"
              >
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                  class="text-warning"
                />
              </div>
              <div>
                <h3 class="text-light mb-0">
                  {{ chartSummary.active_users }}
                </h3>
                <span class="text-white-50">Active Users</span>
              </div>
            </div>
          </b-col>
          <!-- Consumed Tokens -->
          <b-col
            v-if="hasPermission('Token Usage Institution')"
            :md="hasPermission('Token Usage Institution') ? 3 : 4"
            class="mb-2"
          >
            <div
              v-b-tooltip.hover.top="'Consumed Tokens / Maximum Tokens'"
              class="stat-card p-3 d-flex align-items-center"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px);"
            >
              <div
                class="stat-icon mr-2 rounded-circle d-flex justify-content-center align-items-center"
                style="background: rgba(255, 159, 67, 0.2); width: 50px; height: 50px;"
              >
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                  class="text-warning"
                />
              </div>
              <div>
                <h3
                  v-if="chartSummary"
                  class="text-light mb-0"
                >
                  {{ chartSummary.consumed_tokens }} / {{ chartSummary.institution ? chartSummary.institution.institution_max_token : '0' }}
                </h3>
                <span class="text-white-50">Consumed Tokens</span>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Chart Section -->
        <b-row
          v-if="chartSeriesSessions.length > 0 && chartSeriesSessions[0].data.length > 0"
          class="mb-3"
        >
          <!-- Sessions Chart -->
          <b-col
            lg="6"
            cols="12"
            class="mb-3"
          >
            <div
              class="chart-container p-3"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px); height: 100%;"
            >
              <h4 class="text-light mb-2">
                Sessions Overview
              </h4>
              <vue-apex-charts
                type="bar"
                height="240"
                :options="chartOptionsSessions"
                :series="chartSeriesSessions"
              />
            </div>
          </b-col>

          <!-- Users Chart -->
          <b-col
            lg="6"
            cols="12"
            class="mb-3"
          >
            <div
              class="chart-container p-3"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px); height: 100%;"
            >
              <h4 class="text-light mb-2">
                Users Overview
              </h4>
              <vue-apex-charts
                type="bar"
                height="240"
                :options="chartOptionsUsers"
                :series="chartSeriesUsers"
              />
            </div>
          </b-col>

          <!-- Duration Chart -->
          <b-col cols="12">
            <div
              class="chart-container p-3"
              style="background: rgba(255, 255, 255, 0.12); border-radius: 14px; backdrop-filter: blur(18px);"
            >
              <h4 class="text-light mb-2">
                Duration Overview
              </h4>
              <vue-apex-charts
                type="bar"
                height="280"
                :options="chartOptionsDuration"
                :series="chartSeriesDuration"
              />
            </div>
          </b-col>
        </b-row>

        <!-- Tab Navigation (Only shown when an institution is selected) -->
        <div v-if="selectedInstitutionId" class="d-flex align-items-center mb-2 mt-2">
          <div class="dashboard-tabs">
            <button
              type="button"
              class="tab-btn"
              :class="{ active: activeTab === 'users' }"
              @click="activeTab = 'users'"
            >
              <feather-icon
                icon="UsersIcon"
                size="18"
                class="mr-50"
              />
              <span>Users</span>
              <span
                v-if="users.meta && users.meta.total !== undefined"
                class="tab-badge"
              >
                {{ users.meta.total }}
              </span>
            </button>
            <button
              type="button"
              class="tab-btn"
              :class="{ active: activeTab === 'experiments' }"
              @click="activeTab = 'experiments'"
            >
              <feather-icon
                icon="CompassIcon"
                size="18"
                class="mr-50"
              />
              <span>Experiments</span>
              <span
                v-if="selectedInstitution"
                class="tab-badge"
              >
                {{ selectedInstitution.all_experiments ? 'All' : assignedExperimentsList.length }}
              </span>
            </button>
          </div>
        </div>

        <!-- USERS TAB CONTENT -->
        <div v-show="activeTab === 'users'">
          <!-- Search Section -->
          <div
            v-if="selectedInstitutionId"
            class="row text-light pb-3 pt-2 mt-1"
          >
            <div class="col-md-12 mb-2">
              <form
                class="search-form search-form-theme2 w-100"
                @submit.prevent
              >
                <img
                  src="@/assets/images/skeleton/search.png"
                  class="fas fa-search search-icon"
                  alt="search"
                >
                <input
                  v-model="filter.keyword"
                  type="text"
                  placeholder="Search in Users"
                  name="Search"
                  class="search-input"
                >
              </form>
            </div>
          </div>

          <!-- Loading Spinner for users table -->
          <center v-if="selectedInstitutionId && isLoading">
            <b-spinner
              variant="primary"
              label="Loading"
            />
          </center>

          <!-- Users Table -->
          <b-table
            v-if="selectedInstitutionId && !isLoading && users.data && users.data.length > 0"
            id="drag-index"
            :items="users.data"
            :fields="fields"
            striped
            responsive
            class="users"
            no-local-sorting
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            @sort-changed="onSortChanged"
          >
            <template #cell(fname)="data">
              <b-link
                v-if="data.item.fname != null"
                :to="{ name: 'pages_profile', params: { user_id: data.item.id } }"
                class="text-primary font-weight-bold"
              >
                {{ data.item.fname }} {{ data.item.lname }}
              </b-link>
            </template>

            <template #cell(email)="data">
              <div>{{ data.item.email || "N/A" }}</div>
            </template>

            <template #cell(total_sessions)="data">
              <div>{{ data.item.status_counts.total_sessions || 0 }}</div>
            </template>

            <template #cell(total_duration)="data">
              <div>{{ data.item.status_counts.duration_formatted || 0 }}</div>
            </template>

            <template #head(consumed_tokens)="data">
              <span v-b-tooltip.hover.top="'Consumed Tokens / Maximum Tokens'">
                {{ data.label }}
              </span>
            </template>

            <template #cell(consumed_tokens)="data">
              <div>
                {{
                  data.item.consumed_tokens !== undefined && data.item.consumed_tokens !== null
                    ? data.item.consumed_tokens
                    : (data.item.status_counts && data.item.status_counts.consumed_tokens !== undefined && data.item.status_counts.consumed_tokens !== null
                      ? data.item.status_counts.consumed_tokens
                      : 0)
                }}
              </div>
            </template>

            <template #cell(roles)="data">
              <b-badge
                v-for="(value, index) in data.item.roles"
                :key="index"
                pill
                variant="light-primary"
                class="mr-50"
              >
                {{ value }}
              </b-badge>
            </template>

            <template #cell(Actions)="data">
              <div class="actions d-flex align-items-center">
                <b-link :to="{ name: 'pages_profile', params: { user_id: data.item.id } }">
                  <feather-icon
                    v-b-tooltip.hover.top="'Show Profile'"
                    icon="ChevronRightIcon"
                    size="20"
                    class="text-white-50 cursor-pointer"
                  />
                </b-link>
              </div>
            </template>
          </b-table>

          <!-- No Results Message -->
          <div
            v-if="selectedInstitutionId && !isLoading && (!users.data || users.data.length === 0)"
            class="text-center text-light py-5"
          >
            <p>No users found</p>
          </div>

          <!-- Pagination & Per Page -->
          <b-row
            v-if="selectedInstitutionId && users.meta"
            class="mt-2 w-100 align-items-center"
          >
            <b-col md="4" class="text-light d-flex align-items-center" style="font-size: 0.95rem;">
              <span v-if="users.meta.total > 0" class="d-flex align-items-center" style="margin-left: 2px;">
                Showing 
                <b-form-select
                  v-model="filter.per_page"
                  :options="[10, 25, 50, 100]"
                  class="custom-per-page mx-1"
                  @change="onPageChange(1)"
                />
              </span>
            </b-col>
            <b-col md="4" class="d-flex justify-content-center">
              <b-pagination
                v-model="users.meta.current_page"
                :total-rows="users.meta.total"
                :per-page="users.meta.per_page"
                @change="onPageChange"
                class="mb-0"
              />
            </b-col>
            <b-col md="4"></b-col>
          </b-row>
        </div>

        <!-- EXPERIMENTS SELECTION TAB CONTENT (VIEW ONLY) -->
        <div v-if="selectedInstitutionId" v-show="activeTab === 'experiments'" class="mt-2">
          <!-- Loading Spinner -->
          <center v-if="institutionDetailsLoading">
            <b-spinner
              variant="primary"
              label="Loading Experiment Settings"
            />
          </center>

          <div
            v-else
            class="experiments-view-card p-3"
          >
            <!-- Experiments Selection -->
            <div class="mb-3">
              <label class="selection-heading d-block mb-1">
                Experiments Selection
              </label>
              <div class="d-flex align-items-center selection-options">
                <div
                  class="selection-option mr-3"
                  :class="{ selected: experimentSelectionType === 'all' }"
                >
                  <span class="selection-radio" />
                  <span class="selection-text">All Experiments</span>
                </div>
                <div
                  class="selection-option"
                  :class="{ selected: experimentSelectionType === 'custom' }"
                >
                  <span class="selection-radio" />
                  <span class="selection-text">Custom Sellection</span>
                </div>
              </div>
            </div>

            <!-- Experiments * (Shown only when Custom Selection) -->
            <div
              v-if="experimentSelectionType === 'custom'"
              class="mb-1"
            >
              <label class="experiments-input-label d-block mb-1">
                Experiments
              </label>

              <!-- View-only badges container without border, black background, or dropdown icon -->
              <div class="tags-container">
                <div
                  v-for="exp in assignedExperimentsList"
                  :key="exp.id"
                  class="tag-badge"
                >
                  <span class="tag-title">{{ exp.name }}</span>
                </div>
                <span
                  v-if="assignedExperimentsList.length === 0"
                  class="no-tags-text"
                >
                  No experiments assigned
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'
import {
  BTable,
  BRow,
  BCol,
  BBadge,
  BLink,
  BSpinner,
  BPagination,
  VBTooltip,
  BFormSelect,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    VueApexCharts,
    BTable,
    BRow,
    BCol,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
    BFormSelect,
  },

  computed: {
    ...mapGetters({
      users: 'institutions/institutionUsersList',
      experimentsLookups: 'experiments/experimentsLookups',
    }),
    assignedExperimentsList() {
      if (!this.selectedInstitution) return []
      if (this.selectedInstitution.experiments && this.selectedInstitution.experiments.length > 0) {
        return this.selectedInstitution.experiments
      }
      const ids = this.selectedInstitution.experiment_ids || []
      if (ids.length > 0 && this.experimentsLookups && this.experimentsLookups.length > 0) {
        return this.experimentsLookups.filter(exp => ids.includes(exp.id))
      }
      return []
    },
    fields() {
      const baseFields = [
        { key: 'fname', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'total_sessions', label: 'Total Sessions', sortable: true },
        { key: 'total_duration', label: 'Total Duration', sortable: true },
      ]
      if (this.hasPermission('Token Usage User')) {
        baseFields.push({ key: 'consumed_tokens', label: 'Consumed Tokens', sortable: true })
      }
      baseFields.push(
        { key: 'roles', label: 'Roles', sortable: false },
        { key: 'Actions', label: 'Actions', thStyle: { width: '10%' } },
      )
      return baseFields
    },
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      activeTab: 'users',
      selectedInstitutionId: null,
      selectedInstitution: null,
      institutionDetailsLoading: false,
      experimentSelectionType: 'all',
      institutions: [],
      institutionsLoading: false,
      dashboardLoading: false,
      isLoading: false,
      sortBy: 'created_at',
      sortDesc: true,
      filter: {
        keyword: null,
        per_page: 10,
      },
      searchTimeout: null,
      institutionChangeTimeout: null,
      chartSummary: null,
      chartSeriesSessions: [],
      chartSeriesUsers: [],
      chartSeriesDuration: [],
      originalDurations: [],
      chartOptionsSessions: {
        chart: {
          type: 'bar',
          height: 240,
          toolbar: {
            show: true,
            tools: { download: false },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 5,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ['#7367f0'],
        xaxis: {
          type: 'datetime',
          tickAmount: 12,
          categories: [],
          labels: {
            style: { colors: '#fff' },
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: 'MMM',
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
        },
        yaxis: {
          decimalsInFloat: 0,
          title: {
            text: 'Sessions',
            style: { color: '#fff' },
          },
          labels: {
            style: { colors: '#fff' },
            formatter: val => ((val || val === 0) ? Math.round(val) : val),
          },
        },
        fill: {
          type: 'solid',
          opacity: 1,
        },
        tooltip: {
          theme: 'dark',
          x: {
            format: 'dd MMM yyyy',
          },
          y: {
            formatter: val => `${val} sessions`,
          },
        },
        legend: {
          show: false,
        },
      },
      chartOptionsUsers: {
        chart: {
          type: 'bar',
          height: 240,
          toolbar: {
            show: true,
            tools: { download: false },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 5,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ['#ff9f43'],
        xaxis: {
          type: 'datetime',
          tickAmount: 12,
          categories: [],
          labels: {
            style: { colors: '#fff' },
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: 'MMM',
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
        },
        yaxis: {
          decimalsInFloat: 0,
          title: {
            text: 'Active Users',
            style: { color: '#fff' },
          },
          labels: {
            style: { colors: '#fff' },
            formatter: val => ((val || val === 0) ? Math.round(val) : val),
          },
        },
        fill: {
          type: 'solid',
          opacity: 1,
        },
        tooltip: {
          theme: 'dark',
          x: {
            format: 'dd MMM yyyy',
          },
          y: {
            formatter: val => `${val} users`,
          },
        },
        legend: {
          show: false,
        },
      },
      chartOptionsDuration: {
        chart: {
          type: 'bar',
          height: 280,
          toolbar: {
            show: true,
            tools: { download: false },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 5,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ['#28c76f'],
        xaxis: {
          type: 'datetime',
          categories: [],
          labels: {
            style: { colors: '#fff' },
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: 'MMM',
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
        },
        yaxis: {
          decimalsInFloat: 0,
          title: {
            text: 'Duration (hours)',
            style: { color: '#fff' },
          },
          labels: {
            style: { colors: '#fff' },
            formatter: val => (val || val === 0 ? Math.round(val) : val),
          },
        },
        fill: {
          type: 'solid',
          opacity: 1,
        },
        tooltip: {
          theme: 'dark',
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { dataPointIndex }) => {
              const originalVal = this.originalDurations && this.originalDurations[dataPointIndex] !== undefined
                ? this.originalDurations[dataPointIndex]
                : val
              if (originalVal === undefined || originalVal === null) return originalVal
              const totalSeconds = Math.round(originalVal * 3600)
              const h = Math.floor(totalSeconds / 3600)
              const m = Math.floor((totalSeconds % 3600) / 60)
              const s = totalSeconds % 60
              return `${h.toString().padStart(2, '0')}:${m
                .toString()
                .padStart(2, '0')}:${s.toString().padStart(2, '0')}`
            },
          },
        },
        legend: {
          labels: {
            colors: '#fff',
          },
        },
      },
    }
  },

  watch: {
    selectedInstitutionId(newVal) {
      // Defer execution and clear any previous pending selection timeout.
      // A slightly longer delay (150ms) allows the dropdown list to close smoothly
      // and complete its CSS transitions before starting heavy DOM/reactivity updates.
      if (this.institutionChangeTimeout) {
        clearTimeout(this.institutionChangeTimeout)
      }
      this.institutionChangeTimeout = setTimeout(() => {
        this.onInstitutionChange()
      }, 150)
    },
    'filter.keyword': function () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        // Prevent duplicate fetches if the dashboard is already loading
        if (this.selectedInstitutionId && !this.dashboardLoading) {
          this.fetchUsers()
        }
      }, 500)
    },
  },

  mounted() {
    this.fetchInstitutions()
    this.$store.dispatch('experiments/experimentsLookups')
    this.fetchDashboardData()
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.institutionChangeTimeout) {
      clearTimeout(this.institutionChangeTimeout)
    }
  },

  methods: {
    fetchInstitutions() {
      this.institutionsLoading = true
      this.$store
        .dispatch('institutions/institutionLookups')
        .then(res => {
          this.institutions = Array.isArray(res) ? res : res.data || []
          this.institutionsLoading = false
        })
        .catch(e => {
          this.institutionsLoading = false
          this.$swal({
            icon: 'error',
            title: `<h4>Error fetching institutions: ${e.message}</h4>`,
            showConfirmButton: true,
            confirmButtonColor: '#7367f0',
            allowOutsideClick: true,
          })
        })
    },

    onInstitutionChange() {
      this.filter.keyword = null
      if (!this.selectedInstitutionId) {
        this.$store.commit('institutions/SET_INSTITUTION_USERS_LIST', [])
        this.selectedInstitution = null
        this.experimentSelectionType = 'all'
      }
      this.fetchDashboardData()
    },

    fetchDashboardData(page = 1) {
      this.dashboardLoading = true

      const fetchChart = this.fetchChartData(this.selectedInstitutionId)
      const fetchUsersPromise = this.selectedInstitutionId
        ? this.fetchUsers(page)
        : Promise.resolve()
      const fetchInstitutionPromise = this.selectedInstitutionId
        ? this.fetchInstitutionDetails(this.selectedInstitutionId)
        : Promise.resolve()

      Promise.all([fetchChart, fetchUsersPromise, fetchInstitutionPromise])
        .finally(() => {
          this.dashboardLoading = false
        })
    },

    fetchInstitutionDetails(institution_id) {
      if (!institution_id) {
        this.selectedInstitution = null
        this.experimentSelectionType = 'all'
        return Promise.resolve()
      }
      this.institutionDetailsLoading = true
      return this.$store
        .dispatch('institutions/get', institution_id)
        .then(res => {
          const inst = res.institution || res.data || res
          this.selectedInstitution = inst
          if (inst && inst.all_experiments) {
            this.experimentSelectionType = 'all'
          } else {
            this.experimentSelectionType = 'custom'
          }
        })
        .catch(err => {
          console.error('Error fetching institution details:', err)
        })
        .finally(() => {
          this.institutionDetailsLoading = false
        })
    },

    fetchUsers(page = 1) {
      if (!this.selectedInstitutionId) return Promise.resolve()
      this.isLoading = true
      return this.$store
        .dispatch('institutions/institutionUsers', {
          institution_id: this.selectedInstitutionId,
          query: {
            keyword: this.filter.keyword,
            page,
            per_page: this.filter.per_page,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
          },
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    fetchChartData(institution_id) {
      return this.$store
        .dispatch('institutions/experimentSessionsChart', institution_id)
        .then(res => {
          if (res.success && res.data) {
            this.chartSummary = res.data.summary || null
            this.chartOptionsSessions = {
              ...this.chartOptionsSessions,
              xaxis: {
                ...this.chartOptionsSessions.xaxis,
                categories: res.data.labels || [],
              },
            }
            this.chartOptionsUsers = {
              ...this.chartOptionsUsers,
              xaxis: {
                ...this.chartOptionsUsers.xaxis,
                categories: res.data.labels || [],
              },
            }
            this.chartOptionsDuration = {
              ...this.chartOptionsDuration,
              xaxis: {
                ...this.chartOptionsDuration.xaxis,
                categories: res.data.labels || [],
              },
            }
            const parsedDurations = (res.data.durations || []).map(
              timeStr => {
                if (
                  typeof timeStr === 'string'
                  && timeStr.includes(':')
                ) {
                  const parts = timeStr.split(':')
                  if (parts.length === 3) {
                    return (
                      parseInt(parts[0], 10)
                      + parseInt(parts[1], 10) / 60
                      + parseInt(parts[2], 10) / 3600
                    )
                  }
                }
                return 0
              },
            )
            this.originalDurations = parsedDurations
            const maxDuration = Math.max(...parsedDurations, 1)
            const minThreshold = maxDuration * 0.02
            const chartDurations = parsedDurations.map(val => (val > 0 && val < minThreshold ? minThreshold : val))

            this.chartSeriesSessions = [
              {
                name: 'Sessions',
                data: res.data.values || [],
              },
            ]
            this.chartSeriesUsers = [
              {
                name: 'Active Users',
                data: res.data.active_users || [],
              },
            ]
            this.chartSeriesDuration = [
              {
                name: 'Duration',
                data: chartDurations,
              },
            ]
          }
        })
        .catch(err => {
          console.error('Error fetching chart data', err)
        })
    },

    onPageChange(page) {
      this.fetchUsers(page)
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchUsers()
    },
  },
}
</script>

<style scoped lang="scss">
.institution-select {
  background: transparent;
  border-radius: 8px;
  color: #fff;
  padding: 6px 10px;

  option {
    background: #2d2d6e;
    color: #fff;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.4);
    border-color: #7367f0;
  }
}

.dashboard-loader {
  min-height: 200px;
}

.search-form {
  position: relative;
  width: 100%;
  background-color: transparent;
  border: 1px solid #ffffff75;
  border-left: none;
  border-right: none;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.4);
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
}

.search-form-theme2 {
  position: relative;
  width: 100%;
  border-left: none;
  border-right: none;
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
  background-color: #00000040;
  border: 1px solid #ffffff75;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgb(0 0 0 / 65%);
  border-top: unset;
}

.search-form:focus-within {
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
}

.search-form input[type="text"],
.search-form input[type="search"] {
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  outline: none;
}

.search-form input::placeholder {
  color: #ffffffa8;
}

.search-form .search-icon {
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  color: #bbb;
  font-size: 16px;
  cursor: pointer;
}

.actions {
  gap: 8px;
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.dashboard-tabs {
  display: inline-flex;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 4px;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.12);

  .tab-btn {
    display: inline-flex;
    align-items: center;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    padding: 9px 22px;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.06);
    }

    &.active {
      color: #fff;
      background: #7367f0;
      box-shadow: 0 4px 14px rgba(115, 103, 240, 0.4);
    }

    .tab-badge {
      background: rgba(255, 255, 255, 0.2);
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 0.75rem;
      margin-left: 8px;
    }
  }
}

.experiments-view-card {
  background: rgba(18, 26, 43, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(18px);
}

.selection-heading {
  color: #7e879f !important;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  font-weight: 500;
  user-select: none;
}

.selection-options {
  user-select: none;
}

.selection-option {
  display: flex;
  align-items: center;
  cursor: default;

  .selection-radio {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    margin-right: 10px;
    display: inline-block;
    background: #ffffff;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4);
    transition: all 0.25s ease;
  }

  .selection-text {
    color: #ffffff;
    font-size: 1.05rem;
    font-weight: 500;
  }

  &.selected {
    .selection-radio {
      background: #7367f0 !important;
      box-shadow: 0 0 12px rgba(115, 103, 240, 0.8), 0 0 4px #7367f0 !important;
    }
  }
}

.experiments-input-label {
  color: #e2e8f0 !important;
  font-size: 0.95rem;
  font-weight: 600;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 6px 0;

  .tag-badge {
    background: #7367f0;
    color: #ffffff;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.92rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1.4;
    box-shadow: 0 2px 6px rgba(115, 103, 240, 0.4);

    .tag-title {
      white-space: nowrap;
    }
  }

  .no-tags-text {
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.9rem;
    font-style: italic;
    padding: 4px 0;
  }
}
</style>
